import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, Mail, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { API_CONFIG, API_ENDPOINTS } from '../../config/api';
import { api } from '../../services/api';
import { setAuthToken } from '../../utils/authToken';
import { messaging } from '../../config/firebase';
import { getToken } from 'firebase/messaging';

const OTP_LENGTH = 6;

interface LoginProps {
  onLogin: (session: { mobile: string; role: string | null; referralCode?: string; name?: string }) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [loginMethod, setLoginMethod] = useState<'mobile' | 'email'>('mobile');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'enterMobile' | 'enterOtp'>('enterMobile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ title: string; message: string } | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userReferralCode, setUserReferralCode] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    const saved = window.localStorage.getItem('ak_dashboard_session');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.mobile) {
          onLogin({ 
            mobile: parsed.mobile, 
            role: parsed.role || null, 
            referralCode: parsed.referralCode || '',
            name: parsed.name || ''
          });
        }
      } catch {
        // ignore invalid
      }
    }
  }, [onLogin]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const sendMobileOtp = async () => {
    // Animalkart Flow: Send OTP via WhatsApp
    let fcmToken = '';

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        try {
          fcmToken = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY || ''
          });
        } catch (fetchErr: any) {
          // FCM Token generation FAILED
        }
      }
    } catch (tokenErr) {
      console.error('Error in permission wrapper', tokenErr);
    }

    const baseUrl = API_CONFIG.getBaseUrl();
    const res = await api.post(`${baseUrl}/otp/send-whatsapp`, {
      mobile,
      appName: 'animalkart_dashboard', // Always animalkart_dashboard for this flow
      fcmToken, // Passed to backend
    });

    if (res.data?.statuscode !== 200) {
      setError(res.data?.message || 'Failed to send OTP');
      return;
    }

    setUserRole(res.data?.user?.role || null);
    setUserReferralCode(res.data?.user?.referral_code || null);
    setUserName(res.data?.user?.first_name || res.data?.user?.name || null);
    setStep('enterOtp');
    setToast({ title: 'OTP Sent', message: 'OTP sent via WhatsApp.' });
  };

  const sendEmailOtp = async () => {
    const res = await api.post(API_ENDPOINTS.sendEmailOtp(), { email });

    if (res.data?.statuscode !== 200) {
      setError(res.data?.message || 'Failed to send OTP');
      return;
    }

    setUserRole(res.data?.user?.role || null);
    setUserReferralCode(res.data?.user?.referral_code || null);
    setUserName(res.data?.user?.first_name || res.data?.user?.name || null);
    setStep('enterOtp');
    setToast({ title: 'OTP Sent', message: 'OTP sent to your email.' });
  };

  const sendOtp = async () => {
    if (loginMethod === 'mobile') {
      if (!mobile || mobile.length < 10) {
        setError('Please enter a valid mobile number');
        return;
      }
    } else {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    // Clear previous states
    setError(null);
    setLoading(true);

    try {
      if (loginMethod === 'mobile') {
        await sendMobileOtp();
      } else {
        await sendEmailOtp();
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to send OTP';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (otpOverride?: string) => {
    if (loading) return;

    const codeToVerify = otpOverride ?? otp;
    if (!codeToVerify || codeToVerify.length < 4) {
      setError(loginMethod === 'mobile' ? 'Please enter the OTP received on WhatsApp' : 'Please enter the OTP received on email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = loginMethod === 'mobile'
        ? await api.post(`${API_CONFIG.getBaseUrl()}/otp/verify`, { mobile, otp: codeToVerify })
        : await api.post(API_ENDPOINTS.verifyEmailOtp(), { email, otp: codeToVerify });

      if (res.data?.statuscode !== 200) {
        setError(res.data?.message || 'Invalid OTP. Please try again.');
        setLoading(false);
        return;
      }

      // Both verify endpoints issue this JWT on success — store it before
      // anything else in this session makes an API call, since services/api.ts
      // reads it on every request and the backend requires it once
      // IS_AUTH_ENABLED is on (the legacy x-*-mobile headers alone won't do).
      setAuthToken(res.data?.access_token);

      // Prefer the role from the /verify response (returns the full u.role property,
      // e.g. "Investor,Employee") over the value captured at send-OTP time, so a
      // multi-role staff member's session carries every role (drives isAdmin).
      const role = res.data?.user?.role || userRole || 'Animalkart admin';
      const referralCode = res.data?.user?.referral_code || userReferralCode || '';
      const name = res.data?.user?.first_name || res.data?.user?.name || userName || '';
      // Email login still needs a mobile to key the session — the verify response
      // for an email-linked account carries the account's own mobile number.
      const sessionMobile = loginMethod === 'mobile' ? mobile : (res.data?.user?.mobile || '');
      const session = { mobile: sessionMobile, role, referralCode, name };
      // Loading stays true through the success snackbar delay in handleLoginSuccess —
      // it only clears on failure, so the button/inputs stay disabled until the
      // dashboard actually takes over.
      handleLoginSuccess(session);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'OTP verification failed. Please try again.');
      setLoading(false);
    }
  };

  const handleOtpBoxChange = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '');

    if (digits.length > 1) {
      const otpArray = otp.split('');
      for (let i = 0; i < digits.length && index + i < OTP_LENGTH; i++) {
        otpArray[index + i] = digits[i];
      }
      const joined = otpArray.join('').slice(0, OTP_LENGTH);
      setOtp(joined);
      const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1);
      otpInputRefs.current[nextIndex]?.focus();
      // Auto-verify as soon as pasting completes all 6 digits — no button click needed.
      if (joined.length === OTP_LENGTH && otpArray.slice(0, OTP_LENGTH).every(Boolean)) {
        verifyOtp(joined);
      }
      return;
    }

    const otpArray = otp.split('');
    otpArray[index] = digits;
    const joined = otpArray.join('');
    setOtp(joined);

    if (digits && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-verify as soon as the last digit is typed — no button click needed.
    if (digits && joined.length === OTP_LENGTH && otpArray.every(Boolean)) {
      verifyOtp(joined);
    }
  };

  const handleOtpBoxKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'Enter') {
      verifyOtp();
    }
  };

  const handleIdentifierKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendOtp();
    }
  };

  const handleLoginSuccess = (session: any) => {
    setToast({ title: 'Success!', message: 'Welcome to AnimalKart Dashboard' });

    // Delay actual login to show the toast
    setTimeout(() => {
      onLogin(session);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex items-center justify-center p-4">
      {/* Toast */}
      {toast && (
        <div className="fixed top-10 right-1/2 translate-x-1/2 md:translate-x-0 md:right-10 z-[100] animate-in fade-in slide-in-from-top-10 duration-500">
          <div className="bg-emerald-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border border-emerald-500/50 backdrop-blur-sm">
            <div className="bg-white/20 p-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-base">{toast.title}</p>
              <p className="text-white/90 text-sm">{toast.message}</p>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-sm bg-white p-8 rounded-2xl shadow-sm border border-slate-200">
        {/* Brand name */}
        <div className="flex justify-center mb-8">
          <span className="text-2xl font-bold text-slate-900 tracking-tight">AnimalKart Dashboard</span>
        </div>

        <div className="space-y-6">
            <div className="text-center space-y-1">
              <h1 className="text-2xl font-bold text-slate-900">
                Welcome back
              </h1>
              <p className="text-slate-500 text-sm">
                Enter your details to access the dashboard
              </p>
            </div>

            {/* Error/Info Messages */}
            {error && (
              <div className="flex items-center gap-3 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-100 animate-in fade-in zoom-in duration-300">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">{error}</span>
              </div>
            )}

            <div className="space-y-5">
              {/* Login method toggle */}
              {step === 'enterMobile' && (
                <div className="flex items-center bg-slate-100 p-1 rounded-xl">
                  <button
                    type="button"
                    onClick={() => { setLoginMethod('mobile'); setError(null); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${loginMethod === 'mobile' ? 'bg-white text-[#1B4332] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    <Smartphone size={14} /> Mobile
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLoginMethod('email'); setError(null); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${loginMethod === 'email' ? 'bg-white text-[#1B4332] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    <Mail size={14} /> Email
                  </button>
                </div>
              )}

              {/* Mobile Input Field */}
              {loginMethod === 'mobile' ? (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 ml-1 uppercase tracking-wider">Mobile Number</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors">
                      <Smartphone className="h-5 w-5 text-slate-400 group-focus-within:text-[#1B4332]" />
                    </div>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      onKeyDown={handleIdentifierKeyDown}
                      disabled={step === 'enterOtp' || loading}
                      maxLength={10}
                      className="block w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#1B4332] disabled:opacity-60 text-base"
                      placeholder="Enter 10-digit number"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 ml-1 uppercase tracking-wider">Email Address</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors">
                      <Mail className="h-5 w-5 text-slate-400 group-focus-within:text-[#1B4332]" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={handleIdentifierKeyDown}
                      disabled={step === 'enterOtp' || loading}
                      className="block w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#1B4332] disabled:opacity-60 text-base"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>
              )}

              {/* OTP Field - Rendered when step is enterOtp */}
              {step === 'enterOtp' && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-4 duration-500">
                  <label className="text-xs font-semibold text-slate-700 ml-1 uppercase tracking-wider flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5" /> Verification Code
                  </label>
                  <div className="flex items-center justify-between gap-2">
                    {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                      <input
                        key={index}
                        ref={(el) => { otpInputRefs.current[index] = el; }}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={otp[index] || ''}
                        onChange={(e) => handleOtpBoxChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpBoxKeyDown(index, e)}
                        maxLength={1}
                        autoFocus={index === 0}
                        disabled={loading}
                        className="w-full aspect-square min-w-0 text-center border border-slate-200 rounded-xl bg-slate-50 text-slate-900 focus:outline-none focus:border-[#1B4332] disabled:opacity-60 text-lg font-bold"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-2 space-y-4">
                {step === 'enterMobile' ? (
                  <button
                    onClick={sendOtp}
                    disabled={loading}
                    className="w-full flex justify-center py-3.5 px-6 rounded-xl text-sm font-bold text-white bg-[#1B4332] hover:bg-[#163829] active:scale-[0.98] transition-all duration-200 shadow-md shadow-[#1B4332]/20 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Sending Check...</span>
                      </div>
                    ) : 'Continue'}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <button
                      onClick={() => verifyOtp()}
                      disabled={loading}
                      className="w-full flex justify-center py-3.5 px-6 rounded-xl text-sm font-bold text-white bg-[#1B4332] hover:bg-[#163829] active:scale-[0.98] transition-all duration-200 shadow-md shadow-[#1B4332]/20 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Verifying...</span>
                        </div>
                      ) : 'Verify & Login'}
                    </button>

                    <button
                      onClick={() => {
                        setStep('enterMobile');
                        setOtp('');
                        setError(null);
                      }}
                      disabled={loading}
                      className="w-full py-2 text-xs font-semibold text-slate-400 hover:text-[#1B4332] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loginMethod === 'mobile' ? 'Change Mobile Number' : 'Change Email Address'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="pt-6 text-center border-t border-slate-100">
              <p className="text-slate-400 text-xs font-medium tracking-wide">
                © Powered by AnimalKart
              </p>
            </div>
          </div>
        </div>
      </div>
  );
};


export default Login;
